import { AvatarStack, Tag, Text } from '@factorialco/deprecated-design-system'

import { useEffect } from 'modules/core/hooks/useEffect'
import { track, trackingEvents } from 'modules/core/lib/Tracking'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'

import {
  ProjectionKind,
  useEmployeeListForSoftwareDataTable,
} from '../../../generated/resources/datatable.graphql'
import DataTable from '../../core/components/DataTable'
import { useNavigation } from '../../core/hooks/useNavigation'
import { useDebouncedSearch } from '../../core/hooks/useSearch'
import { dateBackendDateTimeToUI } from '../../core/lib/dates'
import i18n from '../../core/lib/i18n'
import { InlineEmployee } from '../../employees/components/InlineEmployee'

const EmployeeSoftwareListPage = () => {
  useEffect(() => {
    track(trackingEvents.itManagement.goToEmployeeList)
  }, [])

  const { debouncedSearch, search, onSearch } = useDebouncedSearch()
  const { navigation } = useNavigation()

  const source = useEmployeeListForSoftwareDataTable(
    [
      {
        identifier: 'employee_fullName',
        title: i18n.t('it_management.software_setups.tabs.employees.table.name'),
        kind: ProjectionKind.Custom,
        render: (employee) => {
          return <InlineEmployee employee={employee} variant="medium" />
        },
        template: 'employee',
      },
      {
        identifier: 'software_logos',
        title: 'Software installed',
        kind: ProjectionKind.Custom,
        render: ({ employeeSoftwareMemberships }) => {
          const avatars = employeeSoftwareMemberships.map((esm) => ({
            url: esm.software ? esm.software.logoUrl : esm.customSoftware!.logoUrl,
            label: esm.software ? esm.software.name : esm.customSoftware!.name,
          }))

          return (
            <AvatarStack
              size="s40"
              overlap="s12"
              avatars={avatars}
              leftover={0}
              textSize={'200'}
              textColor={'grey000White'}
              backgroundColor={'champagne1000'}
            />
          )
        },
        template: 'email',
      },
      {
        identifier: 'employee_team',
        title: i18n.t('it_management.software_setups.tabs.employees.table.team'),
        kind: ProjectionKind.Custom,
        render: ({ memberships }) => {
          return <Tag inline>{memberships[0]?.team?.name}</Tag>
        },
        template: 'team name',
      },
      {
        identifier: 'employee_job',
        title: i18n.t('it_management.software_setups.tabs.employees.table.job'),
        kind: ProjectionKind.Custom,
        render: ({ referenceContract }) => {
          return <Tag inline>{referenceContract?.jobTitle}</Tag>
        },
        template: 'job title',
      },
      {
        identifier: 'startsOn',
        title: i18n.t('it_management.software_setups.tabs.employees.table.member_since'),
        kind: ProjectionKind.Custom,
        render: ({ referenceContract }) => {
          return (
            <Text.Normal size="200">
              {referenceContract?.startsOn
                ? dateBackendDateTimeToUI(referenceContract?.startsOn)
                : ''}
            </Text.Normal>
          )
        },
        template: 'member since',
      },
      {
        identifier: 'location',
        title: i18n.t('it_management.software_setups.tabs.employees.table.location'),
        kind: ProjectionKind.Custom,
        render: ({ locations }) => {
          return <Tag inline>{locations[0]?.name}</Tag>
        },
        template: 'location name',
      },
    ],
    {
      variables: {
        search: debouncedSearch,
      },
    }
  )

  return (
    <ResponsivePage.Body variant="wide">
      <DataTable
        source={source}
        disableDefaultActions
        sort={{ path: 'id', direction: 'desc' }}
        search={{
          placeholder: i18n.t('common.search'),
          value: search,
          onChange: onSearch,
        }}
        rowTo={(employee) =>
          employee.isTerminating
            ? navigation.software.applications.terminatedEmployees.employeeId.fullPath({
                employeeId: employee.id,
              })
            : navigation.software.employees.employeeId.fullPath({ employeeId: employee.id })
        }
      />
    </ResponsivePage.Body>
  )
}

export default EmployeeSoftwareListPage
